import { zodResolver } from '@hookform/resolvers/zod';
import {
  useMutation,
  UseMutationResult,
  useQuery,
} from '@tanstack/react-query';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import { Toast } from 'primereact/toast';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import {
  endpointInvestorGET,
  InvestorGetData,
} from '~/api/def/endpoints/investors';
import { endpointPcValuationEventDELETE } from '~/api/def/endpoints/pcValuationEventEndpoint';
import {
  endpointManualValuationPOST,
  endpointPCompanyValuationEventGET,
  ManualValuationEventPayload,
  ValuationEventResponse,
} from '~/api/def/endpoints/portfolioCompanyEndpoint';
import { agTheme } from '~/components/ui/tables/ag/agTheme';
import { colTypesWidths } from '~/components/ui/tables/ag/columnTypes';
import { currencyList } from '~/config/currenciesStatic';
import { colDefsShareIssuanceTable } from './colDefsShareIssuanceTable';

const validationSchema = z.object({
  investorId: z.string().nonempty('Investor id is required'),
  type: z.string().nonempty('Type is required'),
  value: z
    .string()
    .nonempty('Value is required')
    .regex(/^\d+$/, 'Value must be a number'),
  note: z.string().optional(),
  currency: z.string().nonempty('Currency is required'),
});

const manualEventTypes = {
  expense: 'Expense',
  cash: 'Cash',
};

type ValidationSchema = z.infer<typeof validationSchema>;

interface ShareIssuanceTableProps {
  pCompanyId: string;
  recalculateValuationMutation?: UseMutationResult<void, Error, void, unknown>;
}

export const ShareIssuanceTable = ({
  pCompanyId,
  recalculateValuationMutation,
}: ShareIssuanceTableProps) => {
  const toast = useRef<Toast>(null);
  const [manualEventDialogVisible, setManualEventDialogVisible] =
    useState(false);
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false);
  const [selectedValuationEventId, setSelectedValuationEventId] = useState<
    string | null
  >(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ValidationSchema>({
    resolver: zodResolver(validationSchema),
    defaultValues: {
      type: '',
      value: '',
      note: '',
      currency: '',
      investorId: '',
    },
  });

  const gridRef = useRef<AgGridReact<ValuationEventResponse>>(null);
  const { data, isLoading, refetch, isRefetching } = useQuery({
    queryKey: ['valuationEventGet', pCompanyId],
    queryFn: () => endpointPCompanyValuationEventGET(pCompanyId),
  });

  const { data: investorData, isLoading: investorLoading } = useQuery({
    queryKey: ['investorGet'],
    queryFn: () => endpointInvestorGET(),
  });

  const investors = investorData?.data as InvestorGetData[] | undefined;

  const rowData = data;

  const manualValuationEventMutation = useMutation({
    mutationFn: ({
      pCompanyId,
      payload,
    }: {
      pCompanyId: string;
      payload: ManualValuationEventPayload;
    }) => {
      return endpointManualValuationPOST(pCompanyId, payload);
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Manual event created successfully',
      });
      refetch();
    },
    onError: () => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Something went wrong.',
      });
    },
    onSettled: () => {
      setManualEventDialogVisible(false);
    },
  });

  const deleteValuationEventMutation = useMutation({
    mutationFn: (valuationEventId: string) => {
      return endpointPcValuationEventDELETE(valuationEventId);
    },
    onSuccess: () => {
      toast?.current?.show({
        severity: 'success',
        summary: 'Success',
        detail: 'Valuation event deleted successfully',
      });
      refetch();
      setDeleteDialogVisible(false);
    },
    onError: () => {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: 'Something went wrong while deleting the valuation event.',
      });
      setDeleteDialogVisible(false);
    },
  });

  const onSubmit = async (data: ValidationSchema) => {
    await manualValuationEventMutation.mutateAsync({
      pCompanyId,
      payload: {
        ...data,
        value: Number(data.value),
        note: data.note || '',
        investorId: data.investorId,
      },
    });
  };

  const handleDelete = (valuationEventId: string) => {
    setSelectedValuationEventId(valuationEventId);
    setDeleteDialogVisible(true);
  };

  const confirmDelete = () => {
    if (selectedValuationEventId) {
      deleteValuationEventMutation.mutate(selectedValuationEventId);
    }
  };

  const cancelDelete = () => {
    setDeleteDialogVisible(false);
    setSelectedValuationEventId(null);
  };

  const deleteDialogFooter = (
    <div>
      <Button
        label="No"
        icon="pi pi-times"
        onClick={cancelDelete}
        className="p-button-text"
      />
      <Button
        label="Yes"
        icon="pi pi-check"
        onClick={confirmDelete}
        autoFocus
        severity="danger"
      />
    </div>
  );

  const columnDefs: ColDef<ValuationEventResponse>[] = [
    ...colDefsShareIssuanceTable,
    {
      headerName: 'Actions',
      field: 'id',
      width: 100,
      cellRenderer: (params: ICellRendererParams<ValuationEventResponse>) => (
        <Button
          icon="pi pi-trash"
          severity="danger"
          text
          onClick={(e) => {
            e.stopPropagation();
            handleDelete(params.value);
          }}
        />
      ),
    },
  ];

  const footerContent = (
    <div>
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={() => setManualEventDialogVisible(false)}
        className="p-button-text"
      />
      <Button
        type="submit"
        label="Update"
        icon="pi pi-check"
        onClick={handleSubmit(onSubmit)}
        autoFocus
      />
    </div>
  );

  return (
    <>
      <Toast ref={toast} />

      {/* Custom delete confirmation dialog */}
      <Dialog
        visible={deleteDialogVisible}
        style={{ width: '450px' }}
        header="Delete Confirmation"
        modal
        footer={deleteDialogFooter}
        onHide={cancelDelete}
      >
        <div className="flex align-items-center justify-content-center">
          <i
            className="pi pi-exclamation-triangle mr-3"
            style={{ fontSize: '2rem', color: 'var(--yellow-500)' }}
          />
          <span>Are you sure you want to delete this valuation event?</span>
        </div>
      </Dialog>

      <div className="flex justify-content-between align-content-center mb-3">
        <h3 className="mb-0">Valuation Events</h3>
        <div className="flex gap-2">
          {recalculateValuationMutation && (
            <Button
              label="Recalculate Valuation"
              size="small"
              icon="pi pi-refresh"
              loading={recalculateValuationMutation.status === 'pending'}
              onClick={() => recalculateValuationMutation.mutate()}
            />
          )}
          <Button
            label="Manual Event"
            size="small"
            icon="pi pi-calendar"
            className="w-10rem"
            onClick={() => {
              setManualEventDialogVisible(true);
            }}
          />
        </div>
      </div>
      <div style={{ height: '100%' }}>
        <AgGridReact
          rowData={rowData}
          ref={gridRef}
          loading={isLoading || isRefetching}
          columnDefs={columnDefs}
          columnTypes={colTypesWidths}
          tooltipShowDelay={0}
          theme={agTheme}
          suppressContextMenu
          colResizeDefault="shift"
          suppressCellFocus
          onColumnResized={(params) => {
            if (params.finished) params.api.sizeColumnsToFit();
          }}
          autoSizeStrategy={{
            type: 'fitGridWidth',
            defaultMinWidth: 100,
          }}
        />
      </div>
      <Dialog
        header="Add Manual Event"
        visible={manualEventDialogVisible}
        footer={footerContent}
        onHide={() => {
          if (!manualEventDialogVisible) return;
          setManualEventDialogVisible(false);
        }}
      >
        <div className="flex flex-column gap-2 w-25rem">
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className="grid w-full">
              <div className="col-6 flex flex-column gap-1 w-full">
                <label htmlFor="investor">Investor</label>
                <Controller
                  name="investorId"
                  control={control}
                  rules={{ required: 'Select Investor.' }}
                  render={({ field }) => (
                    <Dropdown
                      style={{ width: '100%' }}
                      disabled={investorLoading}
                      id="investor"
                      {...field}
                      options={investors?.map((investor: InvestorGetData) => ({
                        label: investor.name,
                        value: investor.id,
                      }))}
                    />
                  )}
                />
                {errors.investorId && (
                  <small className="p-error">{errors.investorId.message}</small>
                )}
              </div>
            </div>
            <div className="grid">
              <div className="col-6 flex flex-column gap-1">
                <label htmlFor="type">Type</label>
                <Controller
                  name="type"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="type"
                      {...field}
                      options={Object.keys(manualEventTypes).map((key) => ({
                        label:
                          manualEventTypes[
                            key as keyof typeof manualEventTypes
                          ],
                        value: key,
                      }))}
                    />
                  )}
                />
                {errors.type && (
                  <small className="p-error">{errors.type.message}</small>
                )}
              </div>
              <div className="col-6 flex flex-column gap-1">
                <label htmlFor="value">Value</label>
                <Controller
                  name="value"
                  control={control}
                  render={({ field }) => <InputText id="value" {...field} />}
                />
                {errors.value && (
                  <small className="p-error">{errors.value.message}</small>
                )}
              </div>
              <div className="col-6 flex flex-column gap-1">
                <label htmlFor="shareCount">Note</label>
                <Controller
                  name="note"
                  control={control}
                  render={({ field }) => <InputText id="note" {...field} />}
                />
              </div>
              <div className="col-6 flex flex-column gap-1">
                <label htmlFor="currency">Currency</label>
                <Controller
                  name="currency"
                  control={control}
                  render={({ field }) => (
                    <Dropdown
                      id="currency"
                      filter
                      {...field}
                      options={currencyList}
                      pt={{
                        panel: {
                          style: { width: '2rem' },
                        },
                      }}
                    />
                  )}
                />
                {errors.currency && (
                  <small className="p-error">{errors.currency.message}</small>
                )}
              </div>
            </div>
          </form>
        </div>
      </Dialog>
    </>
  );
};
